import Sprite from './sprite';
import { Graphics } from 'pixi.js';
import { BLEND_MODES } from 'pixi.js';
import Position from './position';
import CollisionBounds from './collisionBounds';
import VectorTools from './vectorTools';

/**
 * 
 */
export default class SpritePoly extends Sprite {

	/**
	 * PIXI graphics object for rendering polygon paths
	 * @param {Object} args
	 * @param {String} args.layer name of layer within current scene to be added to, defaults to 'default'
	 * @param {Position} args.position starting position
	 * @param {*} args.shape string or array of strings describing continuous lines of space-separated x y coords, eg. ['0 0 50 0 50 250 0 250','0 0 10 0 10 10 0 10']
	 * @param {Number} args.border if > 0 a rectangular bounding box will be drawn around the provided shape
	 * @param {Number} args.color rgb hex color eg. 0x00ff00
	 * @param {Number} args.cycleRate color cycle rate for palette sequencing
	 * @param {Number} args.lineWeight scale-independent line weight
	 * @param {Boolean} args.closeShapes draw line from end path vertex to start, of each shape?
	 * @param {Boolean} args.interactive respond to mouse/touch events?
	 * @param {Number} args.redrawPriority graphics will be redrawn every N frames maximum, after a cooldown period. redrawThreshold can be set to lower numbers temporarily for priority redraws, but will eventially 'cool down' to the priority maximum defined here (default 1 = redraw every frame).
	 * @param {Boolean} args.ignoreSystemMods if true (default false), global system graphics modifiers (wobble, voltage slop, etc) will not be added to local sprite values
	 * @param {Number} args.charX for text rendering: X distance between char paths inserted when an 'X' is found in shape data
	 * @param {Number} args.charY for text rendering: Y distance between char paths inserted when a 'Y' is found in shape data
	 * @param {String} args.align for text rendering: X alignment left|right (defaults to center if not present)
	 */
	constructor(args) {
		super(args);
		this.initPaths = true; // generate paths from shape data on init
	}

	init(entity) {
		super.init(entity);

		let args = this._args;

		this.position = args.position || new Position();

		// color stuff
		this.baseColor = args.color || 0x00ee55;
		this.color = this.baseColor;
		this.colorSequence = [];
		this.cycleRate = args.cycleRate || 1 / this.system.timeNormal * 5; // miliseconds per color-frame
		this.cycleMode = 'once'; // once|loop
		this.cycleTime = 0;
		this.cyclePointer = 0;
		this.cycleCounter = 0;
		this.redrawFunction = this.redrawGraphics.bind(this);
		
		// PIXI display object
		this.g = new Graphics();
		this.g.blendMode = BLEND_MODES.ADD;
		this.g.eventMode = args.interactive ? 'dynamic' : 'none';
		this.interactive = args.interactive;
		this.g._lineStyle.cap = 'square';
		this.bounds = new CollisionBounds();
		this.layerName = args.layer || 'default';
		this.layer = this.scene.layers[this.layerName];
		this.layer.container.addChild(this.g);
		
		// vector display modifiers
		this.ignoreSystemMods = args.ignoreSystemMods || false;
		this.alpha = 0.7;
		this.wobble = 0;
		this.slop = 0;
		this.tracers = 0;
		this.lineWeight = args.lineWeight || 2;

		// collision bounding box settings
		this.ignoreBoxRotation = false;
		this.manualCollisionZones = false;
		this.boxScale = 1;

		// runtime temp vars
		this._alpha = 0;
		this._wobble = 0;
		this._slop = 0;
		this._tracers = 0;
		this._lineWeight = 0;
		this._b = null;
		this._vt = VectorTools.get();
		
		// placeholder linestyle
		this.g.lineStyle(this.lineWeight, this.color, 0.5, 0.5, false);

		// setup polygon verts
		if(args.shape) {
			if(typeof args.shape == 'string') args.shape = [args.shape];
			if(this.initPaths) this.paths = this._vt.generatePathsFromShape(args);
		}

		// redraw priority
		this.redrawPriority = args.redrawPriority || 1;
		this.redrawProgress = 1;
		this.redrawThreshold = 1;
		this.redrawCooldown = 1;
	}

	update(delta, deltaNormal) {
		super.update(delta, deltaNormal);

		this.updateColorSequencer(delta);
		this.updateGraphicTransform();

		// re-render lines?
		this.redrawProgress += 1;
		if(this.redrawProgress >= this.redrawThreshold) {
			this.redrawProgress = 0;
			this.refreshDisplayMods();
			this.redrawFunction();
			if(this.redrawThreshold < this.redrawPriority) {
				this.redrawThreshold += this.redrawCooldown;
			}
		}

		this.getCollisionGeometry();
	}

	updateColorSequencer(delta) {
		if(this.cycleTime > 0) {
			this.cycleCounter += delta;
			if(this.cycleCounter >= this.cycleRate) {
				this.cycleCounter = 0;
				this.color = this.colorSequence[this.cyclePointer%this.colorSequence.length];
				this.cyclePointer++;
			}
			this.cycleTime -= delta;
			if(this.cycleTime <= 0) {
				if(this.cycleMode == 'loop') {
					this.cycleTime = Number.MAX_SAFE_INTEGER;
				} else {
					this.color = this.baseColor;
				}
			}
		}
	}

	updateGraphicTransform() {
		this.g.x = this.position.x;
		this.g.y = this.position.y;
		this.g.rotation = this.position.rotation;
		this.g.scale.set(this.position.scale);
		if(this.position.flipX) this.g.scale.x = -this.position.scale;
		if(this.position.flipY) this.g.scale.y = -this.position.scale;
	}

	refreshDisplayMods() {
		// global display mods + local entity mods
		if(this.ignoreSystemMods) {
			this._wobble = this.wobble;
			this._slop = this.slop;
			this._tracers = this.tracers;
			this._lineWeight = this.lineWeight;
		} else {
			this._wobble = this.wobble + this._vt.getSetting('accuracy');
			this._slop = this.slop + this._vt.getSetting('voltageSlop');
			this._tracers = this.tracers + this._vt.getSetting('tracers');
		}
		this._lineWeight = this.lineWeight * this._vt.getSetting('beamWidth');
		this._wobble /= this.position.scale; // consistent despite scale
		this._wobble *= this.layer.inverseZoom; // consistent despite zoom
		this._lineWeight /= this.position.scale; // consistent despite scale
		this._lineWeight *= this.layer.inverseZoom; // consistent despite zoom
	}

	redrawGraphics() {
		this.g.clear();
		// pen-up to start (tracers from 0,0 look crap)
		if(this.paths.length > 0) this.moveTo(this.paths[0][0][0], this.paths[0][0][1]);
		// draw around vertices within all sub-paths
		for(let verts of this.paths) {
			// 'unwanted' faint line drawn to start position
			if(this._tracers > 0) {
				this._alpha = this.alpha*this._tracers*Math.random();
				if(this._alpha > 0) {
					// draw tracer
					this.setLineStyle();
					this.lineTo(verts[0][0], verts[0][1]);
				} else {
					// zero alpha, pen-up to start
					this.moveTo(verts[0][0], verts[0][1]);
				}
			} else {
				// pen-up to start
				this.moveTo(verts[0][0], verts[0][1]);
			}
			// draw around all vertices in this sub-path
			for(let v=1; v<verts.length; v++) {
				if(Math.random() > this._tracers) {
					this.drawLine(verts[v][0], verts[v][1]);
				}
			}
			if(this._args.closeShapes) {
				// draw back to start
				this.drawLine(verts[0][0], verts[0][1]);
			}
		}
	}

	setMultiColorMode() {
		// Special mode only used by SpritePolySparks:
		// The first vertex pair within each individual path of this.paths
		// is expected to have a third element declaring the line's color.
		// To maintain the single-color vector graphic aesthetic of V99,
		// general game objects should avoid using this ...
		// tracers are not drawn in in multicolor mode
		this.redrawFunction = this.redrawMultiColor.bind(this);
	}

	redrawMultiColor() {
		this.g.clear();
		// pen-up to start (tracers from 0,0 look crap)
		if(this.paths.length > 0) this.moveTo(this.paths[0][0][0], this.paths[0][0][1]);
		for(let verts of this.paths) {
			// set color
			this.color = verts[0][2];
			// 'unwanted' faint line drawn to start position
			if(this._tracers > 0) {
				this._alpha = this.alpha*this._tracers*Math.random();
				if(this._alpha > 0) {
					// draw tracer
					this.setLineStyle();
					this.lineTo(verts[0][0], verts[0][1]);
				} else {
					// zero alpha, pen-up to start
					this.moveTo(verts[0][0], verts[0][1]);
				}
			} else {
				// pen-up to start
				this.moveTo(verts[0][0], verts[0][1]);
			}
			// draw around all vertices in this sub-path
			for(let v=1; v<verts.length; v++) {
				if(Math.random() > this._tracers) {
					this.drawLine(verts[v][0], verts[v][1]);
				}
			}
			if(this._args.closeShapes) {
				// draw back to start
				this.drawLine(verts[0][0], verts[0][1]);
			}
		}
	}

	drawLine(x, y) {
		this._alpha = this._vt.offset(this.alpha, this._slop);
		if(this._alpha > 0) {
			// draw line
			this.setLineStyle();
			this.lineTo(x, y);
		} else {
			// zero alpha, skip line
			this.moveTo(x, y);
		}
	}

	setLineStyle() {
		this.g.lineStyle(this._lineWeight, this.color, Math.min(this._alpha, 1), 0.5, false);
		this.g._lineStyle.cap = 'square';
	}

	moveTo(x, y) {
		this.g.moveTo(this._vt.offset(x, this._wobble), this._vt.offset(y, this._wobble));
	}

	lineTo(x, y) {
		this.g.lineTo(this._vt.offset(x, this._wobble), this._vt.offset(y, this._wobble));
	}

	getCollisionGeometry() {
		// ...for mouse clicks
		if(this.interactive) {
			this.g.hitArea = this.g.getLocalBounds();
		}

		if(this.manualCollisionZones || this.entity.bitgroup == 0) return;

		// ...for sprite-to-sprite collision (non physics)
		if(this.ignoreBoxRotation) {
			// use untransformed bounds to avoid this issue: https://github.com/pixijs/pixijs/issues/7346
			this._b = this.g.getLocalBounds();
			this._b.width *= this.position.scale * this.boxScale;
			this._b.height *= this.position.scale * this.boxScale;
			// OPTI: could pre-calc the width+height whenever this.position.scale is set?
		} else {
			// use post-transform bounds
			this._b = this.g.getBounds();
			this._b.width *= this.layer.inverseZoom;
			this._b.height *= this.layer.inverseZoom;
			// OPTI: could pre-calc the width+height whenever this.position.scale is set?
		}

		// local -> global (layer)
		this._b.x = this.position.x - this._b.width*0.5;
		this._b.y = this.position.y - this._b.height*0.5;
		// 'misuse' pixi getters to output a custom bounding box...
		this.bounds.t = this._b.top;
		this.bounds.r = this._b.right; 
		this.bounds.b = this._b.bottom;
		this.bounds.l = this._b.left;
		// centre, and approx circular radius (average dimensions)
		this.bounds.cx = this.position.x;
		this.bounds.cy = this.position.y;
		this.bounds.radius = (this._b.width + this._b.height) * 0.25;
		// OPTI: could pre-calc radius whenever this.position.scale is set?
	}

	/**
	 * @param {Array} sequence array of hex colors eg. [0x880000,0x008800]
	 * @param {String} mode 'once' or 'loop'
	 * @param {Number} time duration of cycling
	 */
	startColorSequence(sequence, mode='once', time=500) {
		this.colorSequence = sequence;
		this.cycleMode = mode;
		this.cyclePointer = 0;
		this.cycleCounter = Number.MAX_SAFE_INTEGER;
		if(mode == 'once') this.cycleTime = time;
		if(mode == 'loop') this.cycleTime = Number.MAX_SAFE_INTEGER;
	}

	stopColorSequence() {
		this.colorSequence = [];
		this.cycleTime = 0;
		this.cycleMode = 'once';
		this.color = this.baseColor;
	}

	hide() {
		this.g.visible = false;
	}

	destroy() {
		this.layer.container.removeChild(this.g);
		this.g = null;
		this.bounds = null;
	}

}